/**
 * Shared Arabic vocabulary for the OWNER BILLING WORKSHEET — the internal accounting
 * page where Host Me's monthly commission is calculated and handed to the accountant.
 *
 * The backend answers in CODES only (`be/config/ownerBilling.js` → `BILLING_WARNINGS`)
 * because the API is language-neutral and the client owns every user-facing string
 * (`docs/03_Technical/I18N_STRATEGY.md`). THIS FILE IS THE OTHER HALF. Same pattern and
 * same reasoning as `config/arrivalLabels.js`, `relocationLabels.js` and
 * `prebookingLabels.js`: agreeing on the data while retyping the sentences is drift
 * that is invisible in a diff.
 *
 * ⚠️ MUST MATCH BACKEND. Every key here is a code in `be/config/ownerBilling.js`. A code
 * with no entry falls back to the code itself — visible, ugly, and preferable to a
 * warning that silently renders as nothing on a page about money.
 *
 * ⚠️ ARABIC-ONLY, deliberately. This is finance-staff and accountant territory: the page
 * is hardcoded RTL and the roles that reach it (`BILLING_READ_ROLES`) are Arabic-speaking
 * back-office users. Putting these in the bilingual locale files would imply a translated
 * surface that does not exist. If billing ever joins the translated surface, these
 * constants become t() calls in each consumer — the import boundary is what keeps that small.
 *
 * Plan: tasks/internal-accounting-page.md
 */

/**
 * Why a row or a property is flagged. Severity comes from the BACKEND
 * ('error' | 'warning' | 'info') and decides the colour — never re-derive it here,
 * because severity is a domain fact (whether the row could be billed at all), not styling.
 */
export const BILLING_WARNING_LABELS = {
  // Revenue could not be established → the row is NOT billed.
  revenue_awaiting_manual_entry: 'إيراد حجز Gathern غير مُدخل — لم يُحتسب في الفاتورة',
  revenue_missing_amount: 'مبلغ الحجز غير متوفر — لم يُحتسب في الفاتورة',
  revenue_unknown_footing: 'معاملة الضريبة لهذه المنصة غير محددة — لم يُحتسب في الفاتورة',
  revenue_unclassified: 'تعذّر تحديد سبب عدم احتساب الإيراد — يُرجى مراجعة الدعم الفني',

  // The two errors nobody notices until an owner does.
  co_owned_property: 'الوحدة مسجّلة بأكثر من مالك نشط — الرسوم كاملة على كل مالك (احتمال تحصيل مزدوج)',
  ownership_starts_in_period: 'تاريخ ربط المالك بالوحدة يقع داخل شهر الفوترة — قد توجد إيرادات سابقة غير مُفوترة',
  legacy_ownership_link: 'الوحدة مرتبطة بالمالك عبر الحقل القديم فقط — يُنصح بتحديث سجل الملكية',

  // Billed, but flagged.
  booking_state_failed: 'حالة معالجة الحجز "فشل" — مُدرج في الفاتورة، يُرجى التحقق',
  booking_state_conflicted: 'الحجز متعارض — مُدرج في الفاتورة، يُرجى التحقق',

  // The VAT behind the fee base.
  vat_not_established: 'ضريبة الحجز غير محددة — احتُسبت الرسوم على إيراد الوحدة وحده',
  vat_footing_drift: 'قيمة الضريبة الواردة من المنصة لا تعادل 15% من الإيراد — يُحتمل أن المنصة غيّرت طريقة احتساب الضريبة · يُرجى المراجعة فورًا قبل اعتماد الشهر',
  vat_inconsistent_with_gross: 'ضريبة المنصة لا تطابق إجمالي الحجز المسجّل — يُرجى المراجعة قبل التسليم',
  vat_footing_assumed: 'أساس الضريبة لهذا الحجز مُفترض من إعدادات المنصة وليس من بيانات الحجز نفسه — للعلم فقط، والمبلغ محتسب كالمعتاد',

  // Billed at the standard rate although it was marked owner-sourced.
  owner_sourced_no_rate: 'الحجز مُسجَّل كحجز من المالك، ولا توجد نسبة متفق عليها في ملفه — احتُسب بالنسبة الأساسية',

  // The stay is billed in instalments across the months it covers.
  long_stay_closed_period: 'هذه الإقامة الطويلة تغطي شهرًا سبق اعتماده ولا يحتوي على قسطها — لم يُضف المبلغ هنا؛ يلزم إعادة فتح ذلك الشهر لتسويته',
  long_stay_frozen_mismatch: 'قسط هذه الإقامة في شهر معتمد سابق يختلف عن قيمته الحالية — تم تعديل الحجز بعد الاعتماد؛ يُرجى المراجعة',

  // Never touches what we charge.
  ota_fee_missing: 'عمولة المنصة غير مسجّلة — للعلم فقط، لا تؤثر على أساس رسوم إدارة هوست مي',
  long_stay_instalment: 'إقامة طويلة تُفوتر بالأقساط — هذا السطر يمثّل حصة هذا الشهر من الإقامة، وليس الحجز كاملًا',
  vat_platform_zero: 'المنصة لم تحصّل ضريبة على هذا الحجز لأن المالك مسجّل ضريبيًا — الضريبة ضمن المبلغ الذي دفعه الضيف ويؤدّيها المالك، واحتُسبت الرسوم على المبلغ دون الضريبة',
  customer_vat_status_missing: 'الحالة الضريبية للعميل غير مسجّلة في ملف الفوترة'
};

/** Resolve a backend warning code to Arabic. Unknown codes show the code itself. */
export const billingWarningLabel = (code) => BILLING_WARNING_LABELS[code] || code;

/** Customer tax identity — mirrors CUSTOMER_TYPES in be/config/ownerBilling.js. */
export const CUSTOMER_TYPE_LABELS = {
  individual: 'فرد',
  business: 'منشأة'
};

/**
 * WHO SOURCED THE BOOKING — the reason a line can carry a rate other than the standard.
 * Mirrors `SOURCED_BY_TIERS` in `be/config/pricingEconomics.js`.
 *
 * ⚠️ Shown ONLY on rows that are not Host-Me-sourced. Labelling every ordinary row
 * «من هوست مي» would put a column of identical words down a page whose whole job is to
 * make the exceptions visible.
 */
export const SOURCED_BY_LABELS = {
  host_me: 'من هوست مي',
  owner: 'من المالك'
};

/**
 * WHERE THE VAT CAME FROM — the accountant's actual request, in one column.
 *
 * He asked to be given CAPTURED figures rather than derived ones, and this is what lets
 * him see at a glance which rows are the platform's own number and which are ours.
 *
 * ⚠️ Labelled on EVERY row, unlike `SOURCED_BY_LABELS` above, and the difference is the
 * distribution. An owner-sourced booking is a rare exception, so marking every ordinary
 * row would bury it. VAT provenance is a genuine three-way split across the book —
 * captured on Airbnb and Booking.com, derived on Gathern, and on direct either keyed by
 * hand (from v1.1.98 onward) or unknown (older rows) — so a blank cell would read as
 * "no information" rather than "the platform's own figure".
 *
 * Mirrors `vatSource` in `be/services/revenueAnalyzer.js` → `resolveBookingVat`.
 */
export const VAT_SOURCE_LABELS = {
  captured: 'من المنصة',
  derived: 'محتسبة',
  none: 'غير محددة'
};

/**
 * ⚠️ A CAPTURED figure on a DIRECT booking did not come from a platform — there is no
 * platform. Ops typed it into the manual booking form (v1.1.98 onward). Printing «من
 * المنصة» on that row defeats the only thing this column is for: telling the accountant
 * where a number actually came from.
 */
const DIRECT_CAPTURED_LABEL = 'مُدخلة يدويًا';

/**
 * A CAPTURED ZERO — the platform reported that it collected no VAT on this booking
 * (Airbnb, once the host's VAT number is on the account). Still «من المنصة»: the source
 * is the platform. The «صفر» is there so a reader does not mistake the row for one where
 * a figure was captured and rounds to nothing, and so the accountant can see at a glance
 * which rows carry the open tax question (tasks/airbnb-vat-inclusive-al-nakheel.md).
 */
const CAPTURED_ZERO_LABEL = 'من المنصة — صفر';

// 'direct' alone — `revenueAnalyzer.normalizePlatform` folds 'onekey' into it before the row
// is built, so listing it too would be dead code implying a decision nobody made
// (the same reasoning `pricingEconomics.PLATFORM_VAT_FOOTING` records).
const DIRECT_PLATFORM = 'direct';

/**
 * Resolve a VAT source to Arabic. Null (a pre-v2 frozen line) shows an em-dash.
 *
 * @param {String} source    'captured' | 'derived' | 'none'
 * @param {String} [platform] the row's platform — only ever narrows the 'captured' wording
 * @param {Number} [value]    the row's VAT — a captured 0 is named as such
 */
export const vatSourceLabel = (source, platform, value) => {
  if (!source) return '—';
  if (source === 'captured' && String(platform || '').toLowerCase() === DIRECT_PLATFORM) {
    return DIRECT_CAPTURED_LABEL;
  }
  if (source === 'captured' && value === 0) return CAPTURED_ZERO_LABEL;
  return VAT_SOURCE_LABELS[source] || source;
};

/**
 * The three columns added when the fee base moved to VAT-inclusive room revenue.
 *
 * Defined here, not typed into the page, for the same reason as `STANDARD_RATE_LABEL`
 * below: the worksheet table and the Excel export both print them, and two spellings of
 * one heading is drift invisible in a diff.
 *
 * ⚠️ `VAT_COLUMN_LABEL` is the VAT the GUEST paid on the room. It is NOT the VAT on Host
 * Me's fee, which appears in the totals block under the same Arabic words. Two taxes on
 * one page — the column header carries «الحجز» so the two can be told apart.
 */
/**
 * The first money column. It used to read «الإيراد بدون ضريبة», which quietly asserted
 * that the figure carries no VAT — true while every platform either added VAT on top or
 * told us it was inside, and NOT established once a VAT-registered host's Airbnb amount
 * arrives with «ضريبة: صفر». The neutral heading states what the column is — the room
 * figure the platform reported, on the footing the row's source describes — and leaves
 * the owner's tax position to the accountant (external consultant, 2026-09-08).
 */
export const REVENUE_COLUMN_LABEL = 'إيراد الوحدة';
export const VAT_COLUMN_LABEL = 'ضريبة القيمة المضافة للحجز';

/**
 * Room revenue PLUS the booking's VAT — the figure that reconciles to the platform's own
 * statement (Booking.com's reservation price; Airbnb's room total with the VAT it collected).
 *
 * ⚠️ Deliberately NOT «إجمالي ما دفعه الضيف». On Airbnb and Gathern the guest also pays a
 * service fee that never reaches the owner, the statement, or us — so "what the guest paid"
 * would be untrue on two of four platforms. The heading names exactly what the column holds
 * (tasks/vat-exclusive-fee-basis.md §7).
 */
export const GROSS_INCL_VAT_COLUMN_LABEL = 'إجمالي الحجز مع الضريبة';
export const GROSS_INCL_VAT_NOTE =
  'إجمالي الحجز مع الضريبة = إيراد الوحدة + ضريبة الحجز، وهو الرقم الذي يطابق كشف المنصة. ' +
  'لا يشمل رسوم الخدمة التي يدفعها الضيف للمنصة نفسها (Airbnb وGathern)، ويُترك فارغًا حين لم تُحدَّد الضريبة.';

/**
 * The booking's VAT TREATMENT — where the VAT sat in the amount the platform sent us. The
 * per-booking fact captured at ingestion (`be/services/vatFooting.js`) and the reason the
 * gross beside it is, or is not, the stored amount. A billing treatment, never the owner's
 * tax position.
 */
export const VAT_TREATMENT_COLUMN_LABEL = 'معاملة الضريبة';
const VAT_TREATMENT_LABELS = {
  inclusive: 'داخل المبلغ',
  exclusive: 'مضافة عليه',
  unknown: 'غير محددة'
};
/**
 * @param {String} footing    the row's `revenueFooting`
 * @param {String} [vatSource] the row's `vatSource` — 'none' means nobody established a VAT,
 *   and then the treatment is not established either. A legacy direct row reads its revenue
 *   off `rate.subtotal` (exclusive by the form's contract) beside a VAT of «—»; printing
 *   «مضافة عليه» there would assert a VAT on top that no one has stated (Adil, dev review
 *   2026-09-09). Says «غير محددة» instead, matching the «مصدر الضريبة» cell beside it.
 */
export const vatTreatmentLabel = (footing, vatSource) => {
  if (!footing) return '—';
  if (vatSource === 'none') return VAT_TREATMENT_LABELS.unknown;
  return VAT_TREATMENT_LABELS[footing] || footing;
};

/**
 * The two column groups (external consultant, round 4): what the PLATFORM reported, then
 * what HOST ME did with it. The split is the audit trail — a reader checks the left group
 * against the platform's statement and the right group against the contract.
 */
export const PLATFORM_FACTS_GROUP_LABEL = 'بيانات المنصة';
export const HOST_ME_CALC_GROUP_LABEL = 'احتساب هوست مي';

/**
 * The finalisation gate (v1.2.17). A month with a booking whose VAT treatment could not be
 * established may be read, never frozen. The fix is one step away and the sentence says so.
 */
export const FINALISE_BLOCKED_NOTE =
  'لا يمكن اعتماد الشهر: معاملة الضريبة غير محددة للحجوزات التالية. يلزم تسجيلها من مستند المنصة أولًا:';
/**
 * The figure the 25% is actually multiplied by.
 *
 * ⚠️ It was «الإيراد مع الضريبة» while the fee billed on a VAT-INCLUSIVE base. The accountant's
 * ruling of 2026-09-09 moved the base to VAT-EXCLUSIVE room revenue, at which point that heading
 * would have sat above a figure with no VAT in it — an owner-facing statement that is simply
 * untrue. Naming it for what it IS survives a basis change in either direction.
 *
 * Not printed since v1.2.17: under the exclusive basis the base equals «إيراد الوحدة» on every
 * row, and two identical columns are noise the accountant has to be told to ignore. The field
 * stays on the payload and the frozen record; this label is where the column comes back from
 * the day the basis moves (tasks/vat-exclusive-fee-basis.md §7).
 */
export const COMMISSION_BASE_COLUMN_LABEL = 'أساس احتساب الرسوم';
export const VAT_SOURCE_COLUMN_LABEL = 'مصدر الضريبة';

/**
 * The default commission rate, named. Defined ONCE because the worksheet and the Excel
 * export both print it, and two spellings of one term is drift invisible in a diff —
 * the same reason every other string on this surface lives in this file.
 */
export const STANDARD_RATE_LABEL = 'النسبة الأساسية';

/** A rate as a percentage for display: 0.15 → «15%». Latin digits, matching `money()`. */
export const ratePercent = (rate) =>
  rate == null || Number.isNaN(Number(rate)) ? '—' : `${Math.round(Number(rate) * 1000) / 10}%`;

/**
 * What the fee is charged ON. The single most important sentence on the page, because it
 * is the one thing a reader cannot work out from a row: the fee column is 25% of the
 * «أساس احتساب الرسوم» column, not of the «إيراد الوحدة» one beside it.
 *
 * ⚠️ Deliberately says «الأساس المتفق عليه» — an AGREED BASIS — and not a word about
 * correcting anything. This was a commercial pricing change, not a VAT correction, and
 * describing it as the latter is how the argument restarts
 * (tasks/billing-vat-inclusive-basis.md §1).
 */
export const BILLING_FEE_BASIS_NOTE =
  'تُحتسب رسوم إدارة هوست مي على إيراد الوحدة بدون ضريبة القيمة المضافة، وقبل خصم عمولة المنصة — وهو الأساس المتفق عليه مع المالك. ' +
  'ضريبة القيمة المضافة على الحجز تخصّ هيئة الزكاة والضريبة والجمارك ولا تدخل في أساس احتساب الرسوم. ' +
  'عمود «مصدر الضريبة» يوضّح مصدر قيمة الضريبة — للعلم فقط، ولا يؤثر على قيمة الرسوم: ' +
  '«من المنصة» واردة من المنصة نفسها · «محتسبة» محتسبة لدينا بنسبة 15% · «غير محددة» لم تُحدَّد بعد.';

/**
 * What the VAT column IS and IS NOT. It is what the platform reported; it is not the
 * owner's VAT liability. Needed the day Airbnb started reporting zero for a VAT-registered
 * host: the sheet must not make the accountant's decision for him by implying that a
 * reported zero means no VAT is owed (tasks/airbnb-vat-inclusive-al-nakheel.md §4.3).
 */
export const PLATFORM_VAT_NOTE =
  'ضريبة الحجز هي ما أبلغت به منصة الحجز نفسها. «صفر» تعني أن المنصة لم تحصّل ضريبة على هذا الحجز، ' +
  'ولا تعني عدم وجود التزام ضريبي على المالك — إن كان المالك مسجّلًا ضريبيًا فالضريبة ضمن المبلغ ويؤدّيها المالك.';


/**
 * ── LONG-STAY INSTALMENTS ───────────────────────────────────────────────────
 *
 * A stay of 28+ nights crossing a month end bills one line per calendar month. These
 * name that on the sheet, so two lines carrying one booking reference never read as a
 * double charge.
 */

/**
 * «قسط 2 من 3».
 *
 * ⚠️ Deliberately NOT `formatProgress()` from `utils/numberFormatter.js`, and the reason
 * is worth keeping. That helper exists for `a/b` — digits joined by a SLASH form one
 * left-to-right number run, so it has to reverse the operands to render correctly in
 * Arabic, and it emits Arabic-Indic digits. Neither applies here: the separator is the
 * word «من», which breaks the run so the natural order is already right, and this sheet
 * is Latin-digit throughout (see `money()` and `ratePercent`). Using the helper would put
 * ٢ and ٣ in a column of 1,234.56 and reverse a fraction that was never a fraction.
 */
export const instalmentLabel = (sequence, of) => `قسط ${sequence} من ${of}`;

/** «من 2026-08-13 إلى 2026-09-01» — the nights this line bills. */
export const periodRangeLabel = (start, end) => `من ${start} إلى ${end}`;

/** Column headings for the period and the stay summary. Printed by the page AND Excel. */
export const PERIOD_START_COLUMN_LABEL = 'بداية الفترة';
export const PERIOD_END_COLUMN_LABEL = 'نهاية الفترة';
export const INSTALMENT_COLUMN_LABEL = 'القسط';
export const STAY_TOTAL_COLUMN_LABEL = 'إجمالي الإقامة';
export const STAY_BILLED_BEFORE_COLUMN_LABEL = 'المفوتر سابقًا';
export const STAY_REMAINING_COLUMN_LABEL = 'المتبقي';

/**
 * The footnote that stops the question. Without it, an accountant seeing one booking
 * reference on two months' sheets reads a duplicate — which is the single most likely
 * misreading this feature introduces.
 *
 * ⚠️ Says «نهاية الفترة» is the departure morning, because `periodEnd` is EXCLUSIVE and a
 * reader who counts it as a night gets a different total from the sheet.
 */
export const LONG_STAY_NOTE =
  'الإقامات الطويلة (28 ليلة أو أكثر وتمتد عبر نهاية شهر) تُفوتر بقسط لكل شهر ميلادي، بحسب عدد ليالي ذلك الشهر ' +
  'من إجمالي ليالي الإقامة، ويستوعب القسط الأخير فروق التقريب حتى يساوي مجموع الأقساط قيمة الإقامة كاملة. ' +
  'ظهور رقم الحجز نفسه في أكثر من شهر ليس تكرارًا. «نهاية الفترة» هي صباح المغادرة ولا تُحتسب ليلة.';

/**
 * The OTAs an owner's VAT registration can be recorded against. ⚠️ MUST MATCH BACKEND —
 * `be/config/ownerBilling.js` → `OTA_VAT_PLATFORMS` is the validator; this is the order the
 * accounts box lists platforms in and the add form's "new account" choices. Keys are
 * `revenueAnalyzer.normalizePlatform()` keys, so `getPlatformDisplayName()` prints them.
 */
export const OTA_VAT_PLATFORMS = ['airbnb', 'booking', 'gathern'];

/**
 * How the room column is footed per platform — the line that stops the accountant reading
 * the Booking.com figure (15% below his extranet) as an error. Printed by the page AND the
 * Excel export, hence a constant. ⚠️ Deliberately does NOT call Airbnb amounts «بدون ضريبة»:
 * for a VAT-registered host Airbnb adds nothing on top, and whether VAT sits inside the
 * amount is the accountant's ruling, not this sentence's.
 */
export const REVENUE_FOOTING_NOTE =
  'رقم الوحدة كما أبلغت به المنصة، على أساس موحّد. مبلغ Booking.com يصل شاملًا الضريبة فيُقسم على 1.15 · ' +
  'Airbnb: المبلغ كما وصل، وضريبة الحجز هي ما حصّلته المنصة فوقه (وقد تكون صفرًا) · ' +
  'Gathern مسجّلة بدون ضريبة وتُستخدم كما هي · الحجز المباشر يُؤخذ قبل الضريبة.';

/**
 * ── «حسابات المنصات والتسجيل الضريبي» (tasks/pricing-listing-build-brainstorm.md, slice 1b) ──
 * One box per owner: each OTA account, its units and +15% rule (READ from Channex's Airbnb copy), and
 * its VAT status as dated change rows (typed). RECORD-ONLY — nothing on this page reads it for money.
 * Codes ⚠️ MUST MATCH BACKEND `be/config/ownerBilling.js` → `OTA_VAT_STATUSES`.
 */
export const OTA_ACCOUNTS_TITLE = 'حسابات المنصات والتسجيل الضريبي';
export const OTA_ACCOUNTS_NOTE = 'للتوثيق فقط، ولا يؤثر على الاحتساب.';
/** Shown with the details only — reference, not day-to-day. */
export const OTA_ACCOUNTS_DETAIL_NOTE =
  'يُعرض كما هو اليوم ولا يُثبَّت مع اعتماد الشهر. وحدات حسابات Airbnb والزيادة على أسعارها تُقرأ من Channex، ' +
  'والحالة الضريبية تُسجَّل يدويًا بتاريخها ودليلها.';

export const OTA_VAT_STATUS_LABELS = {
  registered: 'مسجّل ضريبيًا',
  not_registered: 'غير مسجّل ضريبيًا'
};

/** No row is "nobody has recorded it" — never "not registered" (same tri-state as the VAT profile). */
export const OTA_VAT_NO_RECORD_LABEL = 'لا يوجد سجل';

/** Why a unit is on no Airbnb account — the same three reasons as the pricing card. */
export const OTA_UNLINKED_REASON_LABELS = {
  no_listing: 'لا يوجد رقم إعلان Airbnb في النظام',
  not_read_yet: 'لم تُقرأ من Channex بعد',
  not_on_account: 'لم تعد على حساب Airbnb في آخر قراءة'
};

/** The +15% rule line — same wording as the pricing card «الخصومات والعروض الحالية على Airbnb». */
export const OTA_RATE_RULE_SUFFIX = 'على كل أسعار هذا الحساب في Airbnb، من إعدادات Channex';
export const OTA_RATE_RULE_UNKNOWN_VALUE = 'زيادة على أسعار هذا الحساب — لم تُقرأ النسبة';
export const OTA_RATE_RULE_NONE = 'لا توجد زيادة على أسعار هذا الحساب في Channex';
export const OTA_RATE_RULE_OTHER = 'إعداد آخر على أسعار هذا الحساب في Channex:';

/** Shown when the owner has Airbnb units but none is on a read account yet (prod before the first read). */
export const OTA_AIRBNB_NOT_READ_NOTE = 'تظهر حسابات Airbnb هنا بعد أول قراءة من Channex.';

/** The evidence note's prompt — dates are often only known as a range. */
export const OTA_VAT_NOTE_HINT =
  'من أين عرفت التاريخ؟ إن كان التاريخ تقريبيًا فاذكر المدى، مثال: «ضريبة الحجوزات تظهر من 4–5 سبتمبر».';

/**
 * VAT on HOST ME'S FEE — NOT the guest's VAT on the room, which is `VAT_COLUMN_LABEL`.
 * Two different taxes appear on this page and only the wording keeps them apart. Defined
 * once because the screen's totals block and the Excel export both print it.
 */
export const FEE_VAT_LABEL = 'ضريبة القيمة المضافة على رسوم الإدارة';

/**
 * The line the page must always carry. The billing worksheet and the owner monthly
 * report deliberately do NOT reconcile — the report is accrual and prorated by nights,
 * the bill is whole bookings that checked out in the month. Saying so on the screen is
 * cheaper than answering it every month.
 */

export const BILLING_NOT_THE_REPORT_NOTE =
  'هذه الصفحة تحتسب رسوم الإدارة على الحجوزات التي غادرت خلال الشهر — إضافةً إلى أقساط الإقامات الطويلة التي تغطي ليالي من هذا الشهر — ' +
  'بينما يوزّع التقرير الشهري الإيراد على ليالي الإقامة لكل الحجوزات. الرقمان لا يتطابقان بحكم التصميم.';
